/**
 * @param {ArrayBuffer | Uint8Array} data
 * @returns {string}
 */
export function createPdfObjectUrl(data) {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
}

/**
 * @param {string} title
 * @returns {string}
 */
export function getPdfDownloadFilename(title) {
  const base = typeof title === "string" ? title.trim() : "";
  const filename = base || "documento.pdf";
  return filename.toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`;
}

/**
 * Opens the already-loaded PDF in a new browser tab without leaving the campus view.
 * Uses a native link click so popup blockers do not swallow a window.open of a blob URL.
 *
 * @param {string} objectUrl
 * @returns {Window | null}
 */
export function openPdfInNewTab(objectUrl) {
  if (typeof objectUrl !== "string" || objectUrl.trim() === "") {
    return null;
  }

  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (opened) {
    return opened;
  }

  const link = document.createElement("a");
  link.href = objectUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return null;
}

/**
 * Starts a real file download instead of navigating to the PDF viewer.
 * Mobile browsers often ignore `download` on application/pdf blob URLs and
 * open the document in the current tab; an octet-stream blob avoids that.
 *
 * @param {string} objectUrl
 * @param {string} title
 * @returns {Promise<void>}
 */
export async function triggerPdfDownload(objectUrl, title) {
  if (typeof objectUrl !== "string" || objectUrl.trim() === "") {
    return;
  }

  const filename = getPdfDownloadFilename(title);
  try {
    const response = await fetch(objectUrl);
    const sourceBlob = await response.blob();
    const downloadBlob = new Blob([sourceBlob], { type: "application/octet-stream" });
    const downloadUrl = URL.createObjectURL(downloadBlob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => {
      URL.revokeObjectURL(downloadUrl);
    }, 1500);
  } catch {
    // Keep the current campus view if the browser cannot start the download.
  }
}
